import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ReclamationForm.css';
import FormStep from '../../components/steps/FormStep';
import RecapStep from '../../components/steps/RecapStep';
import ConfirmationStep from '../../components/steps/ConfirmationStep';
import OTPVerificationComponent from '../../components/auth/OTPVerificationComponent';
import { validateForm } from '../../utils/formUtils';
import claimsService from '../../services/claimService';
import { toast } from 'react-toastify';
import { CheckCircle } from 'lucide-react';
import { authService } from '../../services/authService';

const initialState = {
    civilite: '',
    prenom: '',
    nom: '',
    email: '',
    emailConfirm: '',
    telephone: '',
    ville: '',
    pointService: '',
    numeroCompte: '',
    canalReponse: '',
    lieuIncident: '',
    categorie: '',
    element: '',
    objetPlainte: '',
    montantReclame: '',
    dateIncident: '',
    devise: '',
    description: '',
    attente: '',
    isRelance: 'non',
    // Nouvelles propriétés pour les relances
    dateReclamationInitiale: '',
    modeIntroductionInitiale: '',
    precisionAgence: '',
    nomGestionnaire: ''
};

const ReclamationForm = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(0); // 0 = vérification email, 1 = formulaire, 2 = récap, 3 = confirmation
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState(initialState);
    const [files, setFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [trackingNumber, setTrackingNumber] = useState('');
    const [emailVerified, setEmailVerified] = useState(false);

    // Vérifier si un email est déjà vérifié en session
    useEffect(() => {
        const verified = authService.isEmailVerified();
        if (verified) {
            const email = authService.getVerifiedEmail();
            setFormData(prev => ({
                ...prev,
                email: email,
                emailConfirm: email
            }));
            setEmailVerified(true);
            setStep(1); // Passer directement au formulaire
        }
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Empêcher la modification de l'email vérifié
        if (emailVerified && (name === 'email' || name === 'emailConfirm')) {
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Effacer l'erreur quand l'utilisateur modifie le champ
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }

        // Réinitialiser les champs conditionnels si le mode d'introduction change
        if (name === 'modeIntroductionInitiale') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                precisionAgence: '',
                nomGestionnaire: ''
            }));
        }

        // Réinitialiser tous les champs de relance si isRelance change à 'non'
        if (name === 'isRelance' && value === 'non') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                dateReclamationInitiale: '',
                modeIntroductionInitiale: '',
                precisionAgence: '',
                nomGestionnaire: ''
            }));
        }
    };

    const handleFileChange = (e) => {
        setFiles(Array.from(e.target.files));
    };

    // Validation et passage à l'étape récapitulative
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsValidating(true); // Activer indicateur de chargement

        try {
            // Valider le formulaire
            const [isValid, validationErrors] = await validateForm(formData);

            // Mettre à jour l'état des erreurs
            setErrors(validationErrors || {});

            // Si le formulaire n'est pas valide
            if (!isValid) {
                // Faire défiler vers la première erreur
                setTimeout(() => {
                    const firstErrorField = document.querySelector('.is-invalid');
                    if (firstErrorField) {
                        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        try {
                            firstErrorField.focus();
                        } catch (e) {
                            console.log("Impossible de mettre le focus");
                        }
                    }
                }, 50);
                return;
            }

            // Si tout est valide, passer à l'étape suivante
            setStep(2);
        } catch (error) {
            // Erreur technique, pas liée à la validation
            console.error("Erreur technique:", error);
            setErrors({
                submit: error.message || "Une erreur technique est survenue"
            });
        } finally {
            setIsValidating(false);
        }
    };

    // Soumission finale du formulaire
    const handleConfirm = async () => {
        setIsSubmitting(true);
        setErrors({});

        try {
            const formDataToSend = new FormData();

            // Ajouter les champs du formulaire
            Object.entries(formData).forEach(([key, value]) => {
                if (value !== null && value !== undefined && key !== 'emailConfirm') {
                    if (key === 'isRelance') {
                        // Conversion de 'oui'/'non' en booléen pour le backend
                        formDataToSend.append(key, value === 'oui');
                    } else if (key === 'dateIncident' || key === 'dateReclamationInitiale') {
                        // Convertir la date en UTC si elle existe
                        if (value) {
                            const date = new Date(value);
                            formDataToSend.append(key, date.toISOString());
                        }
                    } else {
                        // Ne pas envoyer les champs de relance si ce n'est pas une relance
                        if (formData.isRelance === 'non' &&
                            ['dateReclamationInitiale', 'modeIntroductionInitiale', 'precisionAgence', 'nomGestionnaire'].includes(key)) {
                            // Ignorer ces champs
                        } else {
                            formDataToSend.append(key, value);
                        }
                    }
                }
            });

            // Toujours ajouter le champ Attachments, même vide
            if (files && files.length > 0) {
                files.forEach(file => {
                    formDataToSend.append('Attachments', file);
                });
            }

            console.log("FormData envoyé au serveur:");
            for (let pair of formDataToSend.entries()) {
                console.log(pair[0] + ': ' + pair[1]);
            }

            const responseTrackingNumber = await claimsService.createClaim(formDataToSend);
            setTrackingNumber(responseTrackingNumber);
            setStep(3);

            // Effacer l'email vérifié après une soumission réussie
            authService.clearVerification();

        } catch (error) {
            console.error('Erreur lors de la soumission:', error);
            if (error.name === 'ValidationError') {
                setErrors(prev => ({
                    ...prev,
                    ...error.validationErrors
                }));
                setStep(1);
            } else {
                setErrors(prev => ({
                    ...prev,
                    submit: error.message || "Une erreur s'est produite lors de l'envoi de votre réclamation."
                }));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Gestionnaire pour la vérification OTP réussie
    const handleVerificationComplete = (email) => {
        // Mettre à jour l'email dans le formulaire
        setFormData(prev => ({
            ...prev,
            email: email,
            emailConfirm: email
        }));
        setEmailVerified(true);
        setStep(1); // Passer au formulaire

        // Afficher la notification de succès
        toast.success('Email vérifié avec succès');
    };

    // Indicateur d'étape actuelle
    const StepIndicator = () => {
        const steps = [
            { id: 0, label: "Vérification" },
            { id: 1, label: "Formulaire" },
            { id: 2, label: "Récapitulatif" },
            { id: 3, label: "Confirmation" }
        ];

        return (
            <div className="step-indicator mb-4">
                <div className="step-progress">
                    <div
                        className="step-progress-bar"
                        style={{ width: `${(step / 3) * 100}%` }}
                    ></div>
                </div>

                <div className="step-list">
                    {steps.map((s) => (
                        <div
                            key={s.id}
                            className={`step-item ${s.id === step ? 'active' : ''} ${s.id < step ? 'completed' : ''}`}
                        >
                            <div className="step-item-circle">
                                {s.id < step ? <CheckCircle size={18} /> : s.id + 1}
                            </div>
                            <div className="step-item-label">{s.label}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Bannière indiquant que l'email est vérifié
    const EmailVerifiedBanner = () => {
        if (!emailVerified || step !== 1) return null;

        return (
            <div className="alert alert-success mb-4 d-flex align-items-center">
                <CheckCircle size={18} className="me-2" />
                <div>
                    <strong>Email vérifié :</strong> {formData.email}
                </div>
            </div>
        );
    };

    // Contenu correspondant à l'étape actuelle
    const currentStep = () => {
        switch (step) {
            case 0: // Étape de vérification Email
                return (
                    <OTPVerificationComponent
                        onVerificationComplete={handleVerificationComplete}
                    />
                );
            case 1: // Formulaire
                return (
                    <>
                        <EmailVerifiedBanner />
                        <FormStep
                            formData={formData}
                            handleInputChange={handleInputChange}
                            handleFileChange={handleFileChange}
                            handleSubmit={handleSubmit}
                            errors={errors}
                            emailVerified={emailVerified}
                        />
                    </>
                );
            case 2: // Récapitulatif
                return (
                    <RecapStep
                        formData={{ ...formData, files }}
                        onModify={() => setStep(1)}
                        onConfirm={handleConfirm}
                        isSubmitting={isSubmitting}
                        error={errors.submit}
                    />
                );
            case 3: // Confirmation
                return (
                    <ConfirmationStep
                        trackingNumber={trackingNumber}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="reclamation-container">
            <div className="container py-4">
                {/* Indicateur de progression */}
                <StepIndicator />

                <div className="reclamation-form">
                    {isValidating && (
                        <div className="validation-overlay">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Validation en cours...</span>
                            </div>
                            <p className="mt-2">Validation en cours...</p>
                        </div>
                    )}

                    {currentStep()}
                </div>
            </div>
            <style jsx="true">{`
                .validation-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-color: rgba(255, 255, 255, 0.8);
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    z-index: 1000;
                }
                
                .step-indicator {
                    padding: 20px 10px;
                }
                
                .step-progress {
                    height: 4px;
                    background-color: #e9ecef;
                    border-radius: 4px;
                    position: relative;
                    margin-bottom: 30px;
                }
                
                .step-progress-bar {
                    position: absolute;
                    left: 0;
                    top: 0;
                    height: 100%;
                    background-color: #0A6EBD;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }
                
                .step-list {
                    display: flex;
                    justify-content: space-between;
                    position: relative;
                    margin-top: -48px;
                }
                
                .step-item {
                    flex: 1;
                    text-align: center;
                    padding: 0 5px;
                }
                
                .step-item-circle {
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    background-color: white;
                    border: 2px solid #e9ecef;
                    margin: 0 auto 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    z-index: 1;
                    font-weight: 600;
                    color: #6c757d;
                    transition: all 0.2s ease;
                }
                
                .step-item.active .step-item-circle {
                    border-color: #0A6EBD;
                    color: #0A6EBD;
                    background-color: white;
                }
                
                .step-item.completed .step-item-circle {
                    background-color: #0A6EBD;
                    border-color: #0A6EBD;
                    color: white;
                }
                
                .step-item-label {
                    font-size: 0.85rem;
                    color: #6c757d;
                    transition: all 0.2s ease;
                }
                
                .step-item.active .step-item-label {
                    color: #0A6EBD;
                    font-weight: 600;
                }
                
                .step-item.completed .step-item-label {
                    color: #0A6EBD;
                }
                
                @media (max-width: 576px) {
                    .step-item-label {
                        font-size: 0.7rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default ReclamationForm;